import {
  getLakesideWaitlistArea,
  normalizeWaitlistSchoolId,
} from './waitlistSummaryUtils'

const normalize = (value) => String(value || '').trim().toLowerCase()

export const normalizeWaitlistInstrument = (value) => normalize(value)
  .replace(/\s+/g, ' ')

const getInstrumentValues = (instrument) => {
  if (!instrument) return []
  if (typeof instrument === 'string') return [instrument]

  return [
    instrument.name,
    instrument.parentInstrument && instrument.parentInstrument !== 'Skill'
      ? instrument.parentInstrument
      : null,
    instrument.instrument,
  ].filter(Boolean)
}

export const getTeacherInstrumentNames = (teacher) => {
  const names = new Map()
  ;(Array.isArray(teacher?.InstrumentList) ? teacher.InstrumentList : [])
    .flatMap(getInstrumentValues)
    .forEach((name) => {
      const key = normalizeWaitlistInstrument(name)
      if (key && !names.has(key)) names.set(key, String(name).trim())
    })
  return Array.from(names.values())
}

export const teacherTeachesWaitlistInstrument = (teacher, instrumentName) => {
  const target = normalizeWaitlistInstrument(instrumentName)
  if (!target) return false
  return getTeacherInstrumentNames(teacher).some(
    (name) => normalizeWaitlistInstrument(name) === target
  )
}

export const getWaitlistInstrumentOptions = (summary, locationId = 'all') => {
  const instruments = new Map()
  ;(summary?.schools || []).forEach((school) => {
    if (locationId !== 'all' && school.id !== locationId) return
    ;(school?.instruments || []).forEach((instrument) => {
      if (normalizeWaitlistInstrument(instrument?.name) === 'instrument not listed') return
      const key = normalizeWaitlistInstrument(instrument?.name)
      if (key && !instruments.has(key)) {
        instruments.set(key, String(instrument.name).trim())
      }
    })
  })
  return Array.from(instruments.entries())
    .map(([id, label]) => ({ id, label }))
    .sort((left, right) => left.label.localeCompare(right.label))
}

export const getWaitlistMapLocations = (summary, instrumentName) => {
  const target = normalizeWaitlistInstrument(instrumentName)
  return (summary?.schools || [])
    .filter((school) => school.totalStudents > 0)
    .filter((school) => !target || (school?.instruments || []).some(
      (instrument) => normalizeWaitlistInstrument(instrument?.name) === target
    ))
    .map((school) => ({
      id: school.id,
      label: school.name,
      baseSchoolId: school.baseSchoolId || school.id,
    }))
}

export const buildWaitlistFamilyGroups = ({
  summary,
  instrumentName,
  locationId = 'all',
} = {}) => {
  const target = normalizeWaitlistInstrument(instrumentName)
  const families = new Map()

  ;(summary?.schools || []).forEach((school) => {
    if (locationId !== 'all' && school.id !== locationId) return

    ;(school?.students || []).forEach((student) => {
      if (!(student?.instrumentNames || []).some(
        (name) => normalizeWaitlistInstrument(name) === target
      )) return

      const familyKey = student.familyId
        || `${school.id}:${normalize(student.familyName)}:${normalize(student.city)}`
      if (!families.has(familyKey)) {
        families.set(familyKey, {
          id: familyKey,
          familyId: student.familyId,
          familyName: student.familyName || 'Family',
          schoolId: school.id,
          baseSchoolId: school.baseSchoolId || student.baseSchoolId || school.id,
          schoolName: school.name,
          city: student.city,
          state: student.state,
          familyAddress: student.familyAddress,
          familyLocation: student.familyLocation,
          students: [],
        })
      }

      families.get(familyKey).students.push({
        studentId: student.studentId,
        studentName: student.studentName,
      })
    })
  })

  return Array.from(families.values()).sort((left, right) => (
    left.familyName.localeCompare(right.familyName)
  ))
}

const getTeacherBaseSchoolId = (teacher) => normalizeWaitlistSchoolId(
  teacher?.School || teacher?.school || teacher?.whichSchool
)

const getTeacherAddressCity = (teacher) => String(
  teacher?.Address?.City
  || teacher?.Address?.city
  || teacher?.address?.city
  || teacher?.city
  || ''
).trim()

const getTeacherLakesideArea = (teacher) => {
  const townValues = (Array.isArray(teacher?.TownList) ? teacher.TownList : [])
    .flatMap((town) => [town?.townName, town?.name, town?.id])
    .filter(Boolean)

  if (townValues.some((town) => getLakesideWaitlistArea(town) === 'duluth')) {
    return 'duluth'
  }
  return getLakesideWaitlistArea(getTeacherAddressCity(teacher))
}

export const getTeacherWaitlistLocationId = (teacher) => {
  const schoolId = getTeacherBaseSchoolId(teacher)
  if (schoolId !== 'lsma') return schoolId

  const area = getTeacherLakesideArea(teacher)
  if (area === 'duluth') return 'lsma-duluth'
  if (area === 'review') return 'lsma-location-review'
  return 'lsma-twin-cities'
}

export const getMatchingWaitlistTeachers = ({
  teachers = [],
  instrumentName,
  familyGroups = [],
  locationId = 'all',
} = {}) => {
  const relevantBaseSchools = new Set(
    familyGroups.map((family) => family.baseSchoolId).filter(Boolean)
  )

  return (Array.isArray(teachers) ? teachers : [])
    .filter((teacher) => normalize(teacher?.Status) === 'active')
    .filter((teacher) => teacherTeachesWaitlistInstrument(teacher, instrumentName))
    .filter((teacher) => relevantBaseSchools.has(getTeacherBaseSchoolId(teacher)))
    .filter((teacher) => {
      if (locationId === 'all') return true
      if (locationId === 'lsma-location-review') {
        return getTeacherBaseSchoolId(teacher) === 'lsma'
      }
      return getTeacherWaitlistLocationId(teacher) === locationId
    })
    .sort((left, right) => String(left?.Name || '').localeCompare(String(right?.Name || '')))
}
